import { useMemo } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { NotFound } from "@/components/not-found";
import { PrivateRoutesGuard } from "@/components/private-routes-guard";
import { PublicRoutesGuard } from "@/components/public-routes-guard";
import { AuthPage } from "@/features/auth";
import {
  OrderPhysicalCardPage,
  PhysicalCardSuccessPage,
} from "@/features/card/physical-card";
import {
  CardOnboardingPage,
  NewCardSuccessPage,
  CardDetailsPage,
  CardDisputePage,
  CardFAQDetailsPage,
  CardFAQsPage,
} from "@/features/card/virtual-card";
import { CardTransactionsPage } from "@/features/card/virtual-card/pages/card-transactions";
import { HomePage } from "@/features/home";
import {
  PaymentMethodPage,
  PaymentMethodLayout,
  PaymentMethodPreview,
  SelectPaymentMethodPage,
} from "@/features/payment-methods";
import {
  PaymentsPage,
  PaymentDetailsPage,
  SubmitReceiptPage,
  SubmitMileagePage,
  useClaimDraftCleanup,
} from "@/features/payments";
import { ChangePINPage, SettingsPage } from "@/features/settings";
import { TrialDetailsPage } from "@/features/study";
import { SupportPage } from "@/features/support";
import {
  TaxOnboardingPage,
  CreateTaxFormPage,
  TaxFormSubmissionPage,
  USTaxGuard,
  USTaxPage,
} from "@/features/tax";
import { VisitsPage } from "@/features/visits";

export const AppRoutes = () => {
  const paymentMethodLayout = useMemo(() => <PaymentMethodLayout />, []);

  // Clears claim-flow drafts when navigating away from the submit routes. Hosted
  // here because AppRoutes does not remount on in-app navigation, so the reset
  // keys off real URL transitions and survives spurious page remounts.
  useClaimDraftCleanup();

  if (window.top && window.self !== window.top) {
    // The page is inside an iframe, break out
    window.top.location.href = window.self.location.href;
    return;
  }

  return (
    <Routes>
      <Route element={<PublicRoutesGuard />}>
        {/* Auth */}
        <Route path="/login" element={<AuthPage />} />
      </Route>

      <Route element={<PrivateRoutesGuard />}>
        {/* Payment methods */}
        <Route path="/payment-methods" element={paymentMethodLayout}>
          <Route path="list" element={<SelectPaymentMethodPage />} />
          <Route
            path=":paymentMethodName/create/:formName?"
            element={<PaymentMethodPage />}
          />
          <Route
            path=":paymentMethodName/preview"
            element={<PaymentMethodPreview />}
          />
          <Route path="new-card-onboarding" element={<CardOnboardingPage />} />
          <Route path="new-card-success" element={<NewCardSuccessPage />} />
        </Route>

        {/* Visits */}
        <Route path="/visits" element={<VisitsPage />} />

        {/* Study details */}
        <Route path="/trial" element={<TrialDetailsPage />} />

        {/* Settings */}
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/change-pin" element={<ChangePINPage />} />

        {/* Home */}
        <Route path="/" element={<Navigate replace to="/home" />} />
        <Route path="/home" element={<HomePage />} />

        {/* Payments */}
        <Route path="/payments" element={<PaymentsPage />} />
        <Route
          path="/mileage/:id"
          element={<PaymentDetailsPage paymentKind="mileage" />}
        />
        <Route
          path="/receipt/:id"
          element={<PaymentDetailsPage paymentKind="receipt" />}
        />
        <Route
          path="/micropayment/:id"
          element={<PaymentDetailsPage paymentKind="micropayment" />}
        />
        <Route
          path="/stipend/:id"
          element={<PaymentDetailsPage paymentKind="stipend" />}
        />

        <Route path="/receipt/submit" element={<SubmitReceiptPage />} />
        <Route path="/mileage/submit" element={<SubmitMileagePage />} />

        {/* Virtual card */}
        <Route path="/vcc-card" element={null} />
        <Route path="/card/details" element={<CardDetailsPage />} />
        <Route path="/card/faq" element={<CardFAQsPage />} />
        <Route path="/card/faq/:id" element={<CardFAQDetailsPage />} />
        <Route path="/card/dispute" element={<CardDisputePage />} />
        <Route
          path="/card/:cardType/transactions"
          element={<CardTransactionsPage />}
        />

        {/* Physical card */}
        <Route
          path="/card/physical/order"
          element={<OrderPhysicalCardPage />}
        />
        <Route
          path="/card/physical/success"
          element={<PhysicalCardSuccessPage />}
        />

        {/* Tax */}
        <Route path="/tax" element={<USTaxGuard />}>
          <Route index element={<USTaxPage />} />
          <Route path="success" element={<Navigate replace to="/tax" />} />
          <Route path="cancelled" element={<Navigate replace to="/tax" />} />
          <Route path="onboarding" element={<TaxOnboardingPage />} />
          <Route path="submit" element={<CreateTaxFormPage />} />
          <Route path="form" element={<TaxFormSubmissionPage />} />
        </Route>

        {/* Support */}
        <Route path="/support" element={<SupportPage />} />

        {/* Not found */}
        <Route path="/not-found" element={<NotFound />} />
        <Route path="*" element={<Navigate to="/not-found" />} />
      </Route>
    </Routes>
  );
};
